// Excel Reports Service
import * as XLSX from 'xlsx';
import { formatDate } from '../../utils/formatting';
import { shipmentsService } from '../logistics/shipments';
import { clientsService } from '../logistics/clients';
import { companySettingsService } from '../system/company-settings';
import { reportTemplatesService } from './report-templates';
import { shipmentCostingsService } from '../logistics/shipment-costings';
import { driversService } from '../logistics/drivers';
import { vehiclesService } from '../logistics/vehicles';
import { truckTypesService } from '../logistics/truck-types';
import { saudiCities } from '../../data/saudiCities';

export const excelReportsService = {
  /**
   * Generate Excel report for shipments
   * @param {string} companyId - Company ID
   * @param {Object} options - Report options
   * @param {string} options.templateId - Template ID (optional)
   * @param {Date} options.startDate - Start date for data range
   * @param {Date} options.endDate - End date for data range
   * @param {string} options.clientId - Filter by specific client (optional)
   * @param {string} options.driverId - Filter by specific driver (optional)
   * @param {string} options.vehicleId - Filter by specific vehicle (optional)
   * @param {Array} options.customFields - Custom fields to include
   * @returns {Blob} Excel file blob
   */
  async generateShipmentReport(companyId, options = {}) {
    try {
      console.log('🔄 Generating shipment Excel report...');
      
      // Get company information
      const companySettings = await companySettingsService.getCompanySettings(companyId);
      
      // Get template if specified
      let template = null;
      if (options.templateId) {
        try {
          template = await reportTemplatesService.getTemplate(options.templateId);
        } catch (error) {
          console.warn('Failed to load template, using default fields:', error);
          template = null;
        }
      }
      
      // Get shipments data
      const allShipments = await shipmentsService.getShipments(companyId);

      // Normalize end-of-day so endDate is inclusive of that calendar day.
      const endOfDay = options.endDate ? new Date(options.endDate) : null;
      if (endOfDay) endOfDay.setHours(23, 59, 59, 999);

      // Coerce IDs to strings for comparison — older records may have numeric IDs.
      const eq = (a, b) => a != null && b != null && String(a) === String(b);

      // Filter shipments by date range, client, driver, and vehicle if specified
      let filteredShipments = allShipments.filter(shipment => {
        const shipmentDate = new Date(shipment.pickupDate?.toDate?.() || shipment.pickupDate || shipment.createdAt?.toDate?.() || shipment.createdAt);
        const inDateRange = (!options.startDate || shipmentDate >= options.startDate) &&
                           (!endOfDay || shipmentDate <= endOfDay);
        const matchesClient = !options.clientId || eq(shipment.clientId, options.clientId);
        const matchesDriver = !options.driverId || eq(shipment.driverId, options.driverId);
        const matchesVehicle = !options.vehicleId || eq(shipment.vehicleId, options.vehicleId);
        return inDateRange && matchesClient && matchesDriver && matchesVehicle;
      });

      // Get clients data for names (keyed by stringified id to avoid string/number mismatches)
      const clients = await clientsService.getClients(companyId);
      const clientsMap = clients.reduce((map, client) => {
        map[String(client.id)] = client;
        return map;
      }, {});

      // Get drivers data for names
      const drivers = await driversService.getDrivers(companyId);
      const driversMap = drivers.reduce((map, driver) => {
        map[String(driver.id)] = driver;
        return map;
      }, {});

      // Get vehicles data for truck plates
      const vehicles = await vehiclesService.getVehicles(companyId);
      const vehiclesMap = vehicles.reduce((map, vehicle) => {
        map[String(vehicle.id)] = vehicle;
        return map;
      }, {});

      // Get truck types data for truck kind names
      const truckTypes = await truckTypesService.getTruckTypes(companyId);
      const truckTypesMap = truckTypes.reduce((map, truckType) => {
        map[truckType.value] = truckType;
        return map;
      }, {});

      // Get shipment costings data
      const costings = await shipmentCostingsService.getCostingsByCompany(companyId);
      const costingsMap = costings.reduce((map, costing) => {
        map[costing.shipmentId] = costing;
        return map;
      }, {});

      // Sort shipments based on sortBy option
      const sortedShipments = this.sortShipments(filteredShipments, driversMap, vehiclesMap, options.sortBy || 'date');
      
      // Prepare data for Excel
      const reportData = await this.prepareShipmentData(sortedShipments, clientsMap, driversMap, vehiclesMap, truckTypesMap, costingsMap, template);
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      
      // Create single sheet with header and data
      const combinedSheet = this.createCombinedSheet(companySettings, options, reportData, template);
      
      XLSX.utils.book_append_sheet(workbook, combinedSheet, 'Shipments Report');
      
      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      console.log('✅ Excel report generated successfully');
      return blob;
      
    } catch (error) {
      console.error('❌ Error generating Excel report:', error);
      throw error;
    }
  },

  /**
   * Helper function to return empty string for N/A or 0 values
   */
  getEmptyIfNullOrZero(value) {
    if (value === 'N/A' || value === 0 || value === '0' || !value) {
      return '';
    }
    return value;
  },

  /**
   * Prepare shipment data for Excel export
   */
  async prepareShipmentData(shipments, clientsMap, driversMap, vehiclesMap, truckTypesMap, costingsMap, template) {
    const bilingualHeaders = this.getBilingualHeaders();
    
    const defaultFields = {
      no: { enabled: true, label: bilingualHeaders.no },
      shipmentNumber: { enabled: true, label: bilingualHeaders.shipmentNumber },
      date: { enabled: true, label: bilingualHeaders.date },
      truckPlate: { enabled: true, label: bilingualHeaders.truckPlate },
      truckKind: { enabled: true, label: bilingualHeaders.truckKind },
      pickupSite: { enabled: true, label: bilingualHeaders.pickupSite },
      dropoffSite: { enabled: true, label: bilingualHeaders.dropoffSite },
      numberOfStops: { enabled: true, label: bilingualHeaders.numberOfStops },
      stopsCount: { enabled: true, label: bilingualHeaders.stopsCount },
      stopsList: { enabled: true, label: bilingualHeaders.stopsList },
      stopsByCity: { enabled: true, label: bilingualHeaders.stopsByCity },
      originCity: { enabled: true, label: bilingualHeaders.originCity },
      destinationCities: { enabled: true, label: bilingualHeaders.destinationCities },
      backhaulPrice: { enabled: true, label: bilingualHeaders.backhaulPrice },
      itemizedCosts: { enabled: true, label: bilingualHeaders.itemizedCosts },
      basePrice: { enabled: true, label: bilingualHeaders.basePrice },
      totalPrice: { enabled: true, label: bilingualHeaders.totalPrice },
      clientName: { enabled: true, label: bilingualHeaders.clientName },
      requesterName: { enabled: false, label: bilingualHeaders.requesterName },
      driverName: { enabled: true, label: bilingualHeaders.driverName },
      status: { enabled: true, label: bilingualHeaders.status },
      paymentStatus: { enabled: true, label: bilingualHeaders.paymentStatus },
      notes: { enabled: true, label: bilingualHeaders.notes },
      // Optional fields (disabled by default)
      isInternal: { enabled: false, label: bilingualHeaders.isInternal },
      totalCost: { enabled: false, label: bilingualHeaders.totalCost },
      fuelCost: { enabled: false, label: bilingualHeaders.fuelCost },
      fuelBaseAmount: { enabled: false, label: bilingualHeaders.fuelBaseAmount },
      fuelVATAmount: { enabled: false, label: bilingualHeaders.fuelVATAmount },
      commission: { enabled: false, label: bilingualHeaders.commission },
      maintenanceCost: { enabled: false, label: bilingualHeaders.maintenanceCost },
      maintenanceBaseAmount: { enabled: false, label: bilingualHeaders.maintenanceBaseAmount },
      maintenanceVATAmount: { enabled: false, label: bilingualHeaders.maintenanceVATAmount },
      vehicleInfo: { enabled: false, label: bilingualHeaders.vehicleInfo },
      deliveryDate: { enabled: false, label: bilingualHeaders.deliveryDate },
      distance: { enabled: false, label: bilingualHeaders.distance }
    };

    // Apply template field settings if available, but ALWAYS preserve bilingual labels
    let fields = { ...defaultFields };
    if (template?.fields) {
      // Only merge enabled/disabled settings, NEVER override labels
      Object.keys(defaultFields).forEach(key => {
        if (template.fields[key]) {
          fields[key] = {
            enabled: template.fields[key].enabled,
            label: bilingualHeaders[key] || defaultFields[key].label // Force bilingual labels
          };
        }
      });
    }
    
    // Ensure all fields have bilingual labels
    Object.keys(fields).forEach(key => {
      if (bilingualHeaders[key]) {
        fields[key].label = bilingualHeaders[key];
      }
    });
    
    // Debug logging
    console.log('🔍 Template fields:', template?.fields);
    console.log('🔍 Final fields:', fields);
    console.log('🔍 Enabled fields:', Object.entries(fields).filter(([key, field]) => field.enabled));
    console.log('🔍 Bilingual headers sample:', bilingualHeaders);
    console.log('🔍 Sample field labels:', {
      truckPlate: fields.truckPlate?.label,
      truckKind: fields.truckKind?.label,
      date: fields.date?.label
    });
    
    return shipments.map((shipment, index) => {
      const row = {};
      
      // Get costing data for this shipment
      const costing = costingsMap[shipment.id];
      
      // Add fields based on template or default settings
      if (fields.no?.enabled) {
        row[fields.no.label] = index + 1;
      }
      
      if (fields.shipmentNumber?.enabled) {
        row[fields.shipmentNumber.label] = shipment.shipmentNumber || shipment.trackingNumber || shipment.id;
      }
      
      if (fields.date?.enabled) {
        const date = new Date(shipment.pickupDate || shipment.createdAt);
        row[fields.date.label] = formatDate(date);
      }
      
      if (fields.truckPlate?.enabled) {
        row[fields.truckPlate.label] = this.getEmptyIfNullOrZero(this.getTruckPlate(shipment, vehiclesMap));
      }
      
      if (fields.truckKind?.enabled) {
        row[fields.truckKind.label] = this.getEmptyIfNullOrZero(this.getTruckKind(shipment, vehiclesMap, truckTypesMap));
      }

      if (fields.pickupSite?.enabled) {
        row[fields.pickupSite.label] = this.getEmptyIfNullOrZero(this.getPickupSite(shipment));
      }

      if (fields.dropoffSite?.enabled) {
        row[fields.dropoffSite.label] = this.getEmptyIfNullOrZero(this.getDropoffSite(shipment));
      }

      if (fields.numberOfStops?.enabled) {
        const stops = this.getNumberOfStops(shipment);
        row[fields.numberOfStops.label] = this.getEmptyIfNullOrZero(stops);
      }

      if (fields.stopsCount?.enabled) {
        const n = this.getNumberOfStops(shipment);
        row[fields.stopsCount.label] = n ? `${n} stops` : '';
      }

      if (fields.stopsList?.enabled) {
        row[fields.stopsList.label] = this.getStopsList(shipment);
      }

      if (fields.stopsByCity?.enabled) {
        row[fields.stopsByCity.label] = this.getStopsByCity(shipment);
      }
      
      if (fields.originCity?.enabled) {
        row[fields.originCity.label] = this.getEmptyIfNullOrZero(this.getOriginCity(shipment));
      }
      
      if (fields.destinationCities?.enabled) {
        row[fields.destinationCities.label] = this.getEmptyIfNullOrZero(this.getDestinationCities(shipment));
      }
      
      if (fields.backhaulPrice?.enabled) {
        const backhaulPrice = shipment.backhaulPrice || 0;
        row[fields.backhaulPrice.label] = this.getEmptyIfNullOrZero(backhaulPrice);
      }
      
      if (fields.itemizedCosts?.enabled) {
        const itemizedTotal = this.getItemizedCostsTotal(shipment);
        row[fields.itemizedCosts.label] = this.getEmptyIfNullOrZero(itemizedTotal);
      }
      
      if (fields.basePrice?.enabled) {
        const basePrice = shipment.cost || shipment.basePrice || 0;
        row[fields.basePrice.label] = this.getEmptyIfNullOrZero(basePrice);
      }
      
      if (fields.totalPrice?.enabled) {
        const totalPrice = this.calculateTotalCost(shipment);
        row[fields.totalPrice.label] = this.getEmptyIfNullOrZero(totalPrice);
      }
      
      if (fields.clientName?.enabled) {
        const client = clientsMap[String(shipment.clientId)];
        const clientName = client ? (client.companyName || client.name) : '';
        row[fields.clientName.label] = this.getEmptyIfNullOrZero(clientName);
      }
      
      if (fields.requesterName?.enabled) {
        const requesterName = shipment.requesterName || '';
        row[fields.requesterName.label] = this.getEmptyIfNullOrZero(requesterName);
      }
      
      if (fields.driverName?.enabled) {
        row[fields.driverName.label] = this.getEmptyIfNullOrZero(this.getDriverName(shipment, driversMap));
      }
      
      if (fields.status?.enabled) {
        row[fields.status.label] = this.getEmptyIfNullOrZero(shipment.status);
      }
      
      if (fields.paymentStatus?.enabled) {
        row[fields.paymentStatus.label] = this.getEmptyIfNullOrZero(shipment.paymentStatus);
      }
      
      if (fields.notes?.enabled) {
        row[fields.notes.label] = shipment.notes || '';
      }
      
      // Optional fields
      if (fields.isInternal?.enabled) {
        row[fields.isInternal.label] = shipment.isExternalDriver ? 'External' : 'Internal';
      }
      
      if (fields.totalCost?.enabled && costing) {
        // Calculate total cost from maintenance, fuel, and commission
        const fuelCost = parseFloat(costing.totalFuelAmount || costing.fuel || 0);
        const maintenanceCost = parseFloat(costing.maintenanceAmount || costing.maintenance || 0);
        const commissionCost = parseFloat(costing.commissionAmount || costing.commission || 0);
        const totalCalculatedCost = fuelCost + maintenanceCost + commissionCost;
        row[fields.totalCost.label] = this.getEmptyIfNullOrZero(totalCalculatedCost);
      }
      
      if (fields.fuelCost?.enabled && costing) {
        const fuelCost = costing.totalFuelAmount || costing.fuel || 0;
        row[fields.fuelCost.label] = this.getEmptyIfNullOrZero(fuelCost);
      }
      
      // Add VAT breakdown for fuel costs
      if (fields.fuelBaseAmount?.enabled && costing) {
        const fuelBaseAmount = costing.fuelBaseAmount || 0;
        row[fields.fuelBaseAmount.label] = this.getEmptyIfNullOrZero(fuelBaseAmount);
      }
      
      if (fields.fuelVATAmount?.enabled && costing) {
        const fuelVATAmount = costing.fuelVATAmount || 0;
        row[fields.fuelVATAmount.label] = this.getEmptyIfNullOrZero(fuelVATAmount);
      }
      
      if (fields.commission?.enabled && costing) {
        const commission = costing.commissionAmount || costing.commission || 0;
        row[fields.commission.label] = this.getEmptyIfNullOrZero(commission);
      }
      
      if (fields.maintenanceCost?.enabled && costing) {
        const maintenanceCost = costing.maintenanceAmount || costing.maintenance || 0;
        row[fields.maintenanceCost.label] = this.getEmptyIfNullOrZero(maintenanceCost);
      }
      
      // Add VAT breakdown for maintenance costs
      if (fields.maintenanceBaseAmount?.enabled && costing) {
        const maintenanceBaseAmount = costing.maintenanceBaseAmount || 0;
        row[fields.maintenanceBaseAmount.label] = this.getEmptyIfNullOrZero(maintenanceBaseAmount);
      }
      
      if (fields.maintenanceVATAmount?.enabled && costing) {
        const maintenanceVATAmount = costing.maintenanceVATAmount || 0;
        row[fields.maintenanceVATAmount.label] = this.getEmptyIfNullOrZero(maintenanceVATAmount);
      }
      
      if (fields.vehicleInfo?.enabled) {
        const vehicle = vehiclesMap[String(shipment.vehicleId)];
        const vehicleInfo = vehicle ? `${vehicle.make} ${vehicle.model} (${vehicle.year})` : '';
        row[fields.vehicleInfo.label] = this.getEmptyIfNullOrZero(vehicleInfo);
      }
      
      if (fields.deliveryDate?.enabled) {
        const deliveryDate = shipment.expectedDeliveryDate || shipment.deliveryDate;
        const dateStr = deliveryDate ? formatDate(new Date(deliveryDate)) : '';
        row[fields.deliveryDate.label] = this.getEmptyIfNullOrZero(dateStr);
      }
      
      if (fields.distance?.enabled) {
        row[fields.distance.label] = this.getEmptyIfNullOrZero(shipment.distance);
      }

      // Re-emit row properties in template field order so Excel columns match user preference
      const orderedRow = {};
      Object.entries(fields)
        .sort(([keyA], [keyB]) => {
          const oA = template?.fields?.[keyA]?.order ?? 99;
          const oB = template?.fields?.[keyB]?.order ?? 99;
          return oA - oB;
        })
        .filter(([, f]) => f.enabled)
        .forEach(([, f]) => {
          if (row[f.label] !== undefined) orderedRow[f.label] = row[f.label];
        });

      return orderedRow;
    });
  },

  /**
   * Create combined sheet with header and data
   */
  createCombinedSheet(companySettings, options, reportData, template) {
    const ws = XLSX.utils.aoa_to_sheet([]);
    
    // Company header (row 1-2)
    XLSX.utils.sheet_add_aoa(ws, [
      [companySettings?.companyName || 'Company Name'],
      []
    ], { origin: 'A1' });
    
    // Report title and date info (rows 3-5)
    const startDate = options.startDate ? formatDate(options.startDate) : 'All Time';
    const endDate = options.endDate ? formatDate(options.endDate) : 'All Time';
    const dateRange = options.startDate || options.endDate ? `${startDate} - ${endDate}` : 'All Time';

    XLSX.utils.sheet_add_aoa(ws, [
      ['SHIPMENTS REPORT | تقرير الشحنات'],
      [`Report Date | تاريخ التقرير: ${formatDate(new Date())}`],
      [`Data Range | نطاق البيانات: ${dateRange}`],
      []
    ], { origin: 'A3' });
    
    // Add data starting from row 7
    if (reportData.length > 0) {
      // Get headers from first data row
      const headers = Object.keys(reportData[0]);
      
      // Debug: Log headers to verify they are bilingual
      console.log('🔍 Excel Headers:', headers);
      console.log('🔍 First row data keys:', Object.keys(reportData[0]));
      console.log('🔍 Sample header values:', headers.slice(0, 5));
      
      // Verify headers are bilingual before adding to sheet
      console.log('🔍 Headers being added to Excel:', headers);
      console.log('🔍 Are headers bilingual?', headers.some(h => h.includes('|')));
      
      // FORCE bilingual headers if they're not already bilingual
      const bilingualHeaders = this.getBilingualHeaders();
      const forcedBilingualHeaders = headers.map(header => {
        // If header is already bilingual, keep it
        if (header.includes('|')) return header;
        
        // Otherwise, find the bilingual version
        const fieldKey = Object.keys(bilingualHeaders).find(key => 
          bilingualHeaders[key] === header || 
          header.toLowerCase().includes(key.toLowerCase())
        );
        
        return fieldKey ? bilingualHeaders[fieldKey] : header;
      });
      
      console.log('🔍 Forced bilingual headers:', forcedBilingualHeaders);
      
      // Add headers (use forced bilingual headers)
      XLSX.utils.sheet_add_aoa(ws, [forcedBilingualHeaders], { origin: 'A7' });
      
      // Add data rows (use original headers for data mapping)
      const dataRows = reportData.map(row => headers.map(header => row[header]));
      XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: 'A8' });
    }
    
    // Apply styling and column widths
    this.applySheetStyling(ws, reportData.length, template);
    
    return ws;
  },

  /**
   * Apply styling to the combined sheet
   */
  applySheetStyling(ws, dataRowCount, template) {
    // Set column widths with improved sizing for bilingual headers
    const columnWidths = this.getColumnWidths(template);
    ws['!cols'] = columnWidths;
    
    // Set row heights for header with better spacing
    ws['!rows'] = [
      { hpt: 30 }, // Company name (increased)
      { hpt: 15 }, // Empty row
      { hpt: 25 }, // Report title (increased)
      { hpt: 18 }, // Report date (increased)
      { hpt: 18 }, // Date range (increased)
      { hpt: 15 }, // Empty row
      { hpt: 35 }  // Data headers (increased for bilingual text)
    ];
    
    // Style company name (A1) with enhanced design
    if (!ws['A1']) ws['A1'] = { v: '' };
    ws['A1'].s = {
      font: { bold: true, sz: 18, color: { rgb: '1F4E79' } },
      alignment: { horizontal: 'left', vertical: 'center' },
      fill: { 
        fgColor: { rgb: 'F8F9FA' },
        patternType: 'solid'
      }
    };
    
    // Style report title (A3) with enhanced design
    if (!ws['A3']) ws['A3'] = { v: '' };
    ws['A3'].s = {
      font: { bold: true, sz: 16, color: { rgb: '2E7D32' } },
      alignment: { horizontal: 'left', vertical: 'center' },
      fill: { 
        fgColor: { rgb: 'F1F8E9' },
        patternType: 'solid'
      }
    };
    
    // Style report date and range with consistent formatting
    if (!ws['A4']) ws['A4'] = { v: '' };
    ws['A4'].s = {
      font: { sz: 12, color: { rgb: '424242' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    };
    
    if (!ws['A5']) ws['A5'] = { v: '' };
    ws['A5'].s = {
      font: { sz: 12, color: { rgb: '424242' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    };
    
    // Style data headers (row 7) with improved bilingual design
    if (dataRowCount > 0) {
      const headerRow = 7;
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: headerRow - 1, c: col });
        if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
        ws[cellAddress].s = {
          font: { 
            bold: true, 
            sz: 13,
            color: { rgb: 'FFFFFF' },
            name: 'Arial'
          },
          fill: { 
            fgColor: { rgb: '1565C0' },
            patternType: 'solid'
          },
          border: {
            top: { style: 'medium', color: { rgb: '0D47A1' } },
            bottom: { style: 'medium', color: { rgb: '0D47A1' } },
            left: { style: 'thin', color: { rgb: '0D47A1' } },
            right: { style: 'thin', color: { rgb: '0D47A1' } }
          },
          alignment: { 
            horizontal: 'center', 
            vertical: 'center',
            wrapText: true
          }
        };
      }
      
      // Style data rows with enhanced alternating colors and better formatting
      for (let row = headerRow; row < headerRow + dataRowCount; row++) {
        const isEvenRow = (row - headerRow) % 2 === 0;
        const backgroundColor = isEvenRow ? 'FFFFFF' : 'F8F9FA';
        
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
          
          // Special formatting for numeric columns (prices, costs)
          const cellValue = ws[cellAddress].v;
          const isNumeric = typeof cellValue === 'number' || (!isNaN(parseFloat(cellValue)) && isFinite(cellValue));
          
          ws[cellAddress].s = {
            font: { sz: 11, color: { rgb: '212121' } },
            fill: { 
              fgColor: { rgb: backgroundColor },
              patternType: 'solid'
            },
            border: {
              top: { style: 'thin', color: { rgb: 'E0E0E0' } },
              bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
              left: { style: 'thin', color: { rgb: 'E0E0E0' } },
              right: { style: 'thin', color: { rgb: 'E0E0E0' } }
            },
            alignment: { 
              horizontal: isNumeric ? 'right' : 'left', 
              vertical: 'center',
              wrapText: true
            }
          };
          
          // Apply number formatting for numeric values
          if (isNumeric && cellValue !== 0) {
            // Check if the number has decimals
            const numValue = parseFloat(cellValue);
            const hasDecimals = numValue % 1 !== 0;
            ws[cellAddress].z = hasDecimals ? '#,##0.00' : '#,##0';
          }
        }
      }
    }
  },

  /**
   * Create header sheet with company info and report details (legacy - kept for reference)
   */
  createHeaderSheet(companySettings, options) {
    const headerData = [];
    
    // Company information
    headerData.push(['SHIPMENTS REPORT']);
    headerData.push([]);
    headerData.push(['Company Information:']);
    headerData.push(['Company Name:', companySettings?.companyName || 'N/A']);
    headerData.push(['Address:', companySettings?.address || 'N/A']);
    headerData.push(['Phone:', companySettings?.phone || 'N/A']);
    headerData.push(['Email:', companySettings?.email || 'N/A']);
    headerData.push(['VAT ID:', companySettings?.vatId || 'N/A']);
    headerData.push([]);
    
    // Report information
    headerData.push(['Report Information:']);
    headerData.push(['Generated Date:', formatDate(new Date())]);

    if (options.startDate || options.endDate) {
      const startDate = options.startDate ? formatDate(options.startDate) : 'N/A';
      const endDate = options.endDate ? formatDate(options.endDate) : 'N/A';
      headerData.push(['Date Range:', `${startDate} - ${endDate}`]);
    }
    
    if (options.templateId) {
      headerData.push(['Template Used:', options.templateId]);
    }
    
    headerData.push([]);
    headerData.push(['Note: This report contains shipment data for the specified period.']);
    
    return headerData;
  },

  /**
   * Get column widths for Excel sheet optimized for bilingual headers
   */
  getColumnWidths(template) {
    const defaultWidths = [
      { wch: 10 },  // No. | الرقم
      { wch: 18 },  // Shipment # | رقم الشحنة
      { wch: 15 },  // Date | التاريخ
      { wch: 18 },  // Truck Plate | اللوحة
      { wch: 20 },  // Truck Kind | نوع الشاحنة
      { wch: 25 },  // Pickup Site | موقع الاستلام
      { wch: 25 },  // Dropoff Site | موقع التسليم
      { wch: 18 },  // Number of Stops | عدد المحطات
      { wch: 22 },  // Origin City | مدينة المنشأ
      { wch: 35 },  // Destination Cities | مدن الوجهة
      { wch: 18 },  // Backhaul Price | سعر العودة
      { wch: 20 },  // Itemized Costs | التكاليف المفصلة
      { wch: 18 },  // Base Price | السعر الأساسي
      { wch: 18 },  // Total Price | السعر الإجمالي
      { wch: 25 },  // Client Name | اسم العميل
      { wch: 25 },  // Driver Name | اسم السائق
      { wch: 15 },  // Status | الحالة
      { wch: 18 },  // Payment Status | حالة الدفع
      { wch: 35 },  // Notes | ملاحظات
      { wch: 20 },  // Internal/External | داخلي/خارجي
      { wch: 18 },  // Total Cost | التكلفة الإجمالية
      { wch: 16 },  // Fuel Cost | تكلفة الوقود
      { wch: 22 },  // Fuel Base Amount | المبلغ الأساسي للوقود
      { wch: 25 },  // Fuel VAT Amount | ضريبة القيمة المضافة للوقود
      { wch: 16 },  // Commission | العمولة
      { wch: 20 },  // Maintenance Cost | تكلفة الصيانة
      { wch: 25 },  // Maintenance Base Amount | المبلغ الأساسي للصيانة
      { wch: 28 },  // Maintenance VAT Amount | ضريبة القيمة المضافة للصيانة
      { wch: 25 },  // Vehicle Info | معلومات المركبة
      { wch: 18 },  // Delivery Date | تاريخ التسليم
      { wch: 15 }   // Distance | المسافة
    ];
    
    // If template specifies different fields, adjust accordingly
    if (template?.fields) {
      const enabledFields = Object.values(template.fields).filter(field => field.enabled);
      // Ensure we have enough column widths for all enabled fields
      while (defaultWidths.length < enabledFields.length) {
        defaultWidths.push({ wch: 18 }); // Default width for additional fields
      }
      // Trim if we have too many
      return defaultWidths.slice(0, enabledFields.length);
    }
    
    return defaultWidths;
  },

  /**
   * Helper functions for data extraction
   */
  getOriginCity(shipment) {
    let cityValue = '';
    if (shipment.routeCities && shipment.routeCities.length > 0) {
      cityValue = shipment.routeCities[0].city || '';
    } else {
      cityValue = shipment.originCity || shipment.origin || '';
    }
    
    // Get proper city name from saudiCities data
    const city = saudiCities.find(c => c.value === cityValue);
    return city ? city.label.en : (cityValue || '');
  },

  getDestinationCities(shipment) {
    let cityValues = [];
    if (shipment.routeCities && shipment.routeCities.length > 1) {
      cityValues = shipment.routeCities.slice(1).map(city => city.city).filter(Boolean);
    } else {
      const destination = shipment.destinationCity || shipment.destination;
      if (destination) cityValues = [destination];
    }
    
    // Get proper city names from saudiCities data
    const cityNames = cityValues.map(cityValue => {
      const city = saudiCities.find(c => c.value === cityValue);
      return city ? city.label.en : cityValue;
    });
    
    return cityNames.length > 0 ? cityNames.join(', ') : '';
  },

  /**
   * Get bilingual header labels
   */
  getBilingualHeaders() {
    return {
      no: 'No. | الرقم',
      shipmentNumber: 'Shipment # | رقم الشحنة',
      date: 'Date | التاريخ',
      truckPlate: 'Truck Plate | اللوحة',
      truckKind: 'Truck Kind | نوع الشاحنة',
      pickupSite: 'Pickup Site | موقع الاستلام',
      dropoffSite: 'Dropoff Site | موقع التسليم',
      numberOfStops: 'Number of Stops | عدد المحطات',
      stopsCount: 'Stops Count | عدد المحطات',
      stopsList: 'Stops (raw) | المحطات (نص خام)',
      stopsByCity: 'Stops by City | المحطات حسب المدينة',
      originCity: 'Origin City | مدينة المنشأ',
      destinationCities: 'Destination Cities | مدن الوجهة',
      backhaulPrice: 'Backhaul Price | سعر العودة',
      itemizedCosts: 'Itemized Costs | التكاليف المفصلة',
      basePrice: 'Base Price | السعر الأساسي',
      totalPrice: 'Total Price | السعر الإجمالي',
      clientName: 'Client Name | اسم العميل',
      requesterName: 'Service Requester | طالب الخدمة',
      driverName: 'Driver Name | اسم السائق',
      status: 'Status | الحالة',
      paymentStatus: 'Payment Status | حالة الدفع',
      notes: 'Notes | ملاحظات',
      isInternal: 'Internal/External | داخلي/خارجي',
      totalCost: 'Total Cost | التكلفة الإجمالية',
      fuelCost: 'Fuel Cost | تكلفة الوقود',
      fuelBaseAmount: 'Fuel Base Amount | المبلغ الأساسي للوقود',
      fuelVATAmount: 'Fuel VAT Amount | ضريبة القيمة المضافة للوقود',
      commission: 'Commission | العمولة',
      maintenanceCost: 'Maintenance Cost | تكلفة الصيانة',
      maintenanceBaseAmount: 'Maintenance Base Amount | المبلغ الأساسي للصيانة',
      maintenanceVATAmount: 'Maintenance VAT Amount | ضريبة القيمة المضافة للصيانة',
      vehicleInfo: 'Vehicle Info | معلومات المركبة',
      deliveryDate: 'Delivery Date | تاريخ التسليم',
      distance: 'Distance | المسافة'
    };
  },

  getItemizedCostsTotal(shipment) {
    if (shipment.itemizedCosts && Array.isArray(shipment.itemizedCosts)) {
      return shipment.itemizedCosts.reduce((total, item) => total + (parseFloat(item.total) || 0), 0);
    }
    return 0;
  },

  calculateTotalCost(shipment) {
    const basePrice = parseFloat(shipment.cost || shipment.basePrice || 0);
    const backhaulPrice = parseFloat(shipment.backhaulPrice || 0);
    const itemizedTotal = this.getItemizedCostsTotal(shipment);
    return basePrice + backhaulPrice + itemizedTotal;
  },

  /**
   * Sort shipments based on the specified criteria
   */
  sortShipments(shipments, driversMap, vehiclesMap, sortBy) {
    return [...shipments].sort((a, b) => {
      switch (sortBy) {
        case 'truckPlate':
          const plateA = this.getTruckPlate(a, vehiclesMap);
          const plateB = this.getTruckPlate(b, vehiclesMap);
          return plateA.localeCompare(plateB);
          
        case 'driver':
          const driverA = this.getDriverName(a, driversMap);
          const driverB = this.getDriverName(b, driversMap);
          return driverA.localeCompare(driverB);
          
        case 'date':
        default:
          const dateA = new Date(a.pickupDate || a.createdAt);
          const dateB = new Date(b.pickupDate || b.createdAt);
          return dateA - dateB; // Oldest first — row 1 = earliest shipment
      }
    });
  },

  /**
   * Get truck plate number
   */
  getTruckPlate(shipment, vehiclesMap) {
    if (shipment.isExternalDriver) {
      // For external drivers, use truck plate from shipment if available
      return shipment.truckPlate || shipment.vehiclePlate || 'External';
    } else {
      // For internal vehicles, get plate from vehicles map
      if (shipment.vehicleId) {
        const vehicle = vehiclesMap[String(shipment.vehicleId)];
        if (vehicle) {
          return vehicle.plateNumber || vehicle.plate || '';
        }
      }
      return '';
    }
  },

  /**
   * Get truck kind name
   */
  getTruckKind(shipment, vehiclesMap, truckTypesMap) {
    // First try to get truck kind from vehicle data
    if (shipment.vehicleId && vehiclesMap[String(shipment.vehicleId)]) {
      const vehicle = vehiclesMap[String(shipment.vehicleId)];
      if (vehicle.truckType && truckTypesMap[vehicle.truckType]) {
        const truckType = truckTypesMap[vehicle.truckType];
        return truckType.name?.en || truckType.name || vehicle.truckType;
      }
      return vehicle.truckType || '';
    }
    
    // Fallback to shipment's truck kind if available
    if (shipment.truckKind && truckTypesMap[shipment.truckKind]) {
      const truckType = truckTypesMap[shipment.truckKind];
      return truckType.name?.en || truckType.name || shipment.truckKind;
    }
    
    return shipment.truckKind || '';
  },

  /**
   * Get pickup site from first city in routeCities
   */
  getPickupSite(shipment) {
    if (shipment.routeCities && shipment.routeCities.length > 0) {
      const firstCity = shipment.routeCities[0];
      return firstCity.site || '';
    }
    
    // Fallback to legacy pickup site
    return shipment.pickupSite || '';
  },

  /**
   * Get dropoff site from last city in routeCities
   */
  getDropoffSite(shipment) {
    if (shipment.routeCities && shipment.routeCities.length > 1) {
      const lastCity = shipment.routeCities[shipment.routeCities.length - 1];
      return lastCity.site || '';
    }
    
    // Fallback to legacy dropoff site
    return shipment.dropoffSite || '';
  },

  /**
   * Get number of stops (count all stops across all cities)
   */
  getNumberOfStops(shipment) {
    if (shipment.routeCities && shipment.routeCities.length > 0) {
      const totalStops = shipment.routeCities.reduce((total, city) => {
        return total + (city.stops ? city.stops.length : 0);
      }, 0);
      return totalStops;
    }
    
    // Fallback to legacy stops count
    return shipment.numberOfStops || 0;
  },

  /**
   * Resolve a city code (e.g. "riyadh") to a display name. Falls back to
   * the raw value (which is also what custom "Other" entries are stored as).
   */
  getCityDisplayName(cityValue) {
    if (!cityValue) return '';
    const c = saudiCities.find(x => x.value === cityValue);
    if (!c) return String(cityValue);
    return c.label?.en || c.label?.ar || cityValue;
  },

  /**
   * Flat list of every stop name across all routeCities, joined by " - ".
   * e.g. routeCities = [{stops:[{name:'44556'},{name:'5544'}]},{stops:[{name:'9988'}]}]
   *      -> "44556 - 5544 - 9988"
   */
  getStopsList(shipment) {
    if (!shipment.routeCities || !Array.isArray(shipment.routeCities)) return '';
    const all = shipment.routeCities.flatMap(c =>
      Array.isArray(c?.stops) ? c.stops.map(s => s?.name).filter(Boolean) : []
    );
    return all.join(' - ');
  },

  /**
   * Stops grouped per city, e.g. "Dammam: 5544 - 0099, Riyadh: 9988".
   * Cities with no stop entries are skipped.
   */
  getStopsByCity(shipment) {
    if (!shipment.routeCities || !Array.isArray(shipment.routeCities)) return '';
    return shipment.routeCities
      .map(c => {
        const stops = Array.isArray(c?.stops)
          ? c.stops.map(s => s?.name).filter(Boolean)
          : [];
        if (stops.length === 0) return null;
        const cityName = this.getCityDisplayName(c.city);
        return `${cityName}: ${stops.join(' - ')}`;
      })
      .filter(Boolean)
      .join(', ');
  },

  /**
   * Get driver name with proper external/internal indication
   */
  getDriverName(shipment, driversMap) {
    if (shipment.isExternalDriver) {
      // For external drivers, use the driver name from shipment and add "ext"
      const driverName = shipment.driverName || shipment.externalDriverName || '';
      return driverName ? `${driverName} ext` : '';
    } else {
      // For internal drivers, get name from drivers map
      if (shipment.driverId) {
        const driver = driversMap[String(shipment.driverId)];
        if (driver) {
          return driver.name || driver.fullName || '';
        }
      }
      // Fallback to shipment driver name without suffix
      return shipment.driverName || '';
    }
  },

  /**
   * Download Excel file
   */
  downloadExcelFile(blob, filename = 'shipments-report.xlsx') {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
};